import deepCopy from 'lodash/cloneDeep';
import has from 'lodash/has';
import uniq from 'lodash/uniq';
import { cancel, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { call, put } from 'store/utils';
import ContactSupportOutlinedIcon from '@material-ui/icons/ContactSupportOutlined';
import {
  askQuestion,
  askForHelp,
  attendeeStatus,
  blockUser,
  getCachedQuestions,
  getConferenceSession,
  presenterStatus,
  setQuestionAnswer,
  setQuestionHidden,
  startBroadcast,
  stopBroadcast,
  updateVodResource,
  upvoteQuestion,
} from 'utils/apiCalls';
import * as conferenceActions from './conferenceActions';
import { setNotificationAction } from 'store/utils/utilActions';

function* askForHelpFlowSaga(action) {
  const key = 'askForHelpFlowSaga';

  yield put(conferenceActions.initAction({ key }));
  try {
    const { Message } = action.payload;
    const { UserId, SessionId } = yield select(({ user, conference }) => ({
      UserId: user.userId,
      SessionId: conference.SessionId,
    }));
    yield call(askForHelp, Message, UserId, SessionId);

    yield put(
      setNotificationAction({
        message: 'Help request received!',
        type: 'info',
        icon: ContactSupportOutlinedIcon,
        notiPopoutName: 'Help Request',
      })
    );

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* blockUserFlowSaga(action) {
  const key = 'blockUserFlowSaga';
  // console.log('in the block user saga');
  yield put(conferenceActions.initAction({ key }));
  try {
    const { BlockUserId, shouldBlock } = action.payload;
    const { SessionId } = yield select(({ conference }) => ({
      SessionId: conference.SessionId,
    }));
    yield call(blockUser, SessionId, BlockUserId, shouldBlock);

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* askQuestionFlowSaga(action) {
  const key = 'askQuestion';
  yield put(conferenceActions.cancelCachQuestionsAction());
  yield put(conferenceActions.initAction({ key }));
  try {
    const { question } = action.payload;
    const { UserId, SessionId, userName, questions, thisUsersActiveQuestionCount } = yield select(
      ({ user, conference }) => ({
        UserId: user.userId,
        SessionId: conference.SessionId,
        userName: `${user.firstName} ${user.lastName}`,
        questions: deepCopy(conference.questions),
        thisUsersActiveQuestionCount: conference.thisUsersActiveQuestionCount,
      })
    );
    const data = yield call(askQuestion, question, UserId, SessionId, userName, Date.now());

    if (data.error) throw Error(data.error.message);

    if (data.blocked) {
      const { blockedUsers } = yield select(({ conference }) => ({
        blockedUsers: conference.blockedUsers,
      }));
      yield put(
        conferenceActions.setBlockedUsersAction({ blockedUsers: uniq([...blockedUsers, UserId]) })
      );
    } else {
      const newQuestion = data.question;
      newQuestion.VoteCount = newQuestion.VoteCount ?? 0;
      newQuestion.didLocalUserVote = newQuestion.didLocalUserVote ?? false;
      questions.push(newQuestion);

      conferenceActions.updateUserQuestionUpdatesAction({
        questionId: newQuestion.QuestionId,
        data: { ...newQuestion },
      });
      yield put(
        conferenceActions.setAllQuestionsAction({
          questions,
          thisUsersActiveQuestionCount: thisUsersActiveQuestionCount + 1,
        })
      );
      yield put(conferenceActions.cancelCachQuestionsAction());
      // console.log('JUST PUT THE NEW QUESTIONS LIST IN', questions);
    }
    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* initCacheSagaWatchers() {
  const cacheWorkerTask = yield takeEvery(
    conferenceActions.getCachedQuestionsAction().type,
    getCachedQuestionsFlowSaga
  );
  yield takeLatest(
    conferenceActions.cancelCachQuestionsAction().type,
    cancelCacheCallFlowSaga,
    cacheWorkerTask
  );
}

function* cancelCacheCallFlowSaga(task) {
  try {
    yield put(conferenceActions.missNextCacheCallAction());
    if (task.isRunning()) yield cancel(task);
  } catch (e) {
    // console.log('THE CANCEL FAILED', e);
  } finally {
    if (task.isCancelled()) yield call(initCacheSagaWatchers);
  }
}

function* getCachedQuestionsFlowSaga() {
  const key = 'getCachedQuestionsFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  try {
    let { SessionId, oldQuestions, userQuestionUpdates, userId } = yield select(
      ({ user, conference }) => ({
        SessionId: conference.SessionId,
        userQuestionUpdates: conference.userQuestionUpdates,
        oldQuestions: deepCopy(conference.questions),
        userId: user.userId,
      })
    );
    let questionsMap = {};
    oldQuestions.forEach((q) => {
      questionsMap[q.QuestionId] = q;
    });
    let thisUsersActiveQuestionCount = 0;
    let questions = ((yield call(getCachedQuestions, SessionId)).questions || []).map(
      (question) => {
        let qid = question.QuestionId;
        if (has(questionsMap, qid)) {
          question = { ...questionsMap[qid], ...question };
        }
        if (has(userQuestionUpdates, qid)) {
          question = { ...question, ...userQuestionUpdates[qid] };
        }
        if (userId === question.UserId) {
          thisUsersActiveQuestionCount += 1;
        }
        return question;
      }
    );
    // console.log('ATTEMPT TO PUT CACHED QUESTIONS ON THERE', questions);

    yield put(
      conferenceActions.setCachedQuestionsAction({
        questions,
        thisUsersActiveQuestionCount,
      })
    );
    yield put(conferenceActions.clearUserQuestionUpdatesAction());

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* upvoteQuestionFlowSaga(action) {
  yield put(conferenceActions.cancelCachQuestionsAction());
  const key = 'upvoteQuestion';
  yield put(conferenceActions.initAction({ key }));
  try {
    const { QuestionId, AddVote } = action.payload;
    const { UserId, SessionId, questions } = yield select(({ user, conference }) => ({
      UserId: user.userId,
      SessionId: conference.SessionId,
      questions: deepCopy(conference.questions),
    }));

    for (let question of questions) {
      if (question.QuestionId === QuestionId) {
        question.VoteCount += AddVote ? 1 : -1;
        yield put(
          conferenceActions.updateUserQuestionUpdatesAction({
            questionId: QuestionId,
            data: { VoteCount: question.VoteCount },
          })
        );
        question.didLocalUserVote = AddVote;
        break;
      }
    }

    yield put(conferenceActions.setAllQuestionsAction({ questions }));
    yield put(conferenceActions.cancelCachQuestionsAction());
    yield call(upvoteQuestion, SessionId, QuestionId, UserId, AddVote);
    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* deleteQuestionFlowSaga(action) {
  yield put(conferenceActions.cancelCachQuestionsAction());
  const key = 'deleteQuestionFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  try {
    const { questionId, isThisUsersQuestion } = action.payload;
    const { UserId, SessionId, questions, thisUsersActiveQuestionCount } = yield select(
      ({ user, conference }) => ({
        UserId: user.userId,
        SessionId: conference.SessionId,
        questions: deepCopy(conference.questions),
        thisUsersActiveQuestionCount: conference.thisUsersActiveQuestionCount,
      })
    );
    yield call(setQuestionHidden, SessionId, UserId, questionId, true);
    yield put(
      conferenceActions.updateUserQuestionUpdatesAction({
        questionId,
        data: { Hidden: true },
      })
    );

    yield put(
      conferenceActions.setAllQuestionsAction({
        questions: questions.filter((q) => q.QuestionId !== questionId),
        thisUsersActiveQuestionCount: isThisUsersQuestion
          ? thisUsersActiveQuestionCount - 1
          : thisUsersActiveQuestionCount,
      })
    );
    yield put(conferenceActions.cancelCachQuestionsAction());

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* setQuestionHiddenFlowSaga(action) {
  yield put(conferenceActions.cancelCachQuestionsAction());
  const key = 'setQuestionHiddenFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  try {
    const { QuestionId, Hidden } = action.payload;
    const { SessionId, questions, UserId } = yield select(({ conference, user }) => ({
      SessionId: conference.SessionId,
      UserId: user.userId,
      questions: deepCopy(conference.questions),
    }));

    for (const q of questions) {
      if (q.QuestionId === QuestionId) {
        q.Hidden = Hidden;
        yield put(
          conferenceActions.updateUserQuestionUpdatesAction({
            questionId: QuestionId,
            data: { Hidden: q.Hidden },
          })
        );
        break;
      }
    }

    yield put(conferenceActions.setAllQuestionsAction({ questions }));
    yield put(conferenceActions.cancelCachQuestionsAction());
    yield call(setQuestionHidden, SessionId, UserId, QuestionId, Hidden);

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* updateVodResourceFlowSaga(action) {
  const key = 'updateVodResourceFlowSaga';
  try {
    yield put(conferenceActions.initAction({ key }));
    const {
      VideoUrl,
      SessionId,
      Name = null,
      Published = null,
      StartTime = null,
      EndTime = null,
    } = action.payload;
    const { Vod } = yield call(
      updateVodResource,
      SessionId,
      VideoUrl,
      Name,
      Published,
      StartTime,
      EndTime
    );
    yield put(conferenceActions.setVodResourcesAction({ Vod }));

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* setQuestionAnswerFlowSaga(action) {
  yield put(conferenceActions.cancelCachQuestionsAction());

  const key = 'answerQuestionFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  try {
    const { QuestionId, Answer } = action.payload;
    const { SessionId, questions, UserId } = yield select(({ conference, user }) => ({
      SessionId: conference.SessionId,
      UserId: user.userId,
      questions: deepCopy(conference.questions),
    }));

    for (const q of questions) {
      if (q.QuestionId === QuestionId) {
        q.Answer = Answer;
        yield put(
          conferenceActions.updateUserQuestionUpdatesAction({
            questionId: QuestionId,
            data: { Answer: q.Answer },
          })
        );
        break;
      }
    }
    yield call(setQuestionAnswer, SessionId, UserId, QuestionId, Answer);
    yield put(conferenceActions.setAllQuestionsAction({ questions }));
    yield put(conferenceActions.cancelCachQuestionsAction());

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* getConferenceSagaFlowSaga(action) {
  const key = 'getConferenceFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  const { role } = yield select(({ user }) => ({
    role: user.role,
  }));
  try {
    const { sessionId } = action.payload;
    const conferenceData = yield call(getConferenceSession, sessionId);
    if (!conferenceData.data) throw new Error('Conference data does not exist');
    if (conferenceData.error) throw new Error(conferenceData.error);
    yield put(conferenceActions.setConferenceAction(conferenceData.data));
    if (role === 'attendee') {
      yield put(conferenceActions.getAttendeeStatusAction());
    } else {
      yield put(conferenceActions.getStatusAction());
    }

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* startConferenceSessionFlowSaga() {
  const key = 'startConferenceSessionFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  try {
    const { sessionId } = yield select(({ conference }) => ({
      sessionId: conference.SessionId,
    }));
    const startData = yield call(startBroadcast, sessionId);

    yield put(conferenceActions.setConferenceAction(startData.data));
    // if (['admin', 'presenter'].includes(userRole.toLowerCase())) {
    //   yield call(updateCloudwatchEvent, 'ENABLED');
    // }
    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* stopConferenceSessionFlowSaga() {
  const key = 'stopConferenceSessionFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  try {
    const { SessionId } = yield select(({ conference }) => ({
      SessionId: conference.SessionId,
    }));
    const stopData = yield call(stopBroadcast, SessionId);

    yield put(conferenceActions.setConferenceAction(stopData.data));
    yield put(conferenceActions.setStatusAction({ Ivs: '', Status: '' }));

    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* sessionStatusFlowSaga() {
  const key = 'sessionStatusFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  try {
    const { SessionId } = yield select(({ conference }) => ({
      SessionId: conference.SessionId,
    }));
    const status = yield call(presenterStatus, SessionId);
    if (status.error) {
      throw Error('No status');
    } else {
      yield put(
        conferenceActions.setBlockedUsersAction({ blockedUsers: status.session.BlockedUsers || [] })
      );
      yield put(conferenceActions.setStatusAction({ ...status.status }));
      yield put(conferenceActions.setConferenceAction(status.session));
    }
    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

function* attendeeStatusFlowSaga() {
  const key = 'sessionStatusFlowSaga';
  yield put(conferenceActions.initAction({ key }));
  try {
    const { SessionId } = yield select(({ conference }) => ({
      SessionId: conference.SessionId,
    }));
    const status = yield call(attendeeStatus, SessionId);
    if (status.error) {
      throw Error('No status');
    } else {
      yield put(
        conferenceActions.setBlockedUsersAction({ blockedUsers: status.session.BlockedUsers || [] })
      );
      yield put(conferenceActions.setStatusAction({ ...status.status }));
      yield put(conferenceActions.setConferenceAction(status.session));
    }
    yield put(conferenceActions.successAction({ key }));
  } catch (error) {
    yield put(conferenceActions.errorAction({ key, error }));
  }
}

export default function* conferenceWatcher() {
  // yield takeEvery(
  //   conferenceActions.setupBroadcastSessionAction().type,
  //   setupBroadcastSessionFlowSaga
  // )
  yield takeEvery(conferenceActions.getConferenceAction().type, getConferenceSagaFlowSaga);
  yield takeEvery(conferenceActions.getStatusAction().type, sessionStatusFlowSaga);
  yield takeEvery(conferenceActions.getAttendeeStatusAction().type, attendeeStatusFlowSaga);
  yield takeEvery(conferenceActions.askQuestionAction().type, askQuestionFlowSaga);
  yield takeEvery(conferenceActions.upvoteQuestionAction().type, upvoteQuestionFlowSaga);
  yield takeEvery(conferenceActions.setQuestionHiddenAction().type, setQuestionHiddenFlowSaga);
  yield takeEvery(conferenceActions.setQuestionAnswerAction().type, setQuestionAnswerFlowSaga);
  yield takeEvery(conferenceActions.deleteQuestionAction().type, deleteQuestionFlowSaga);
  yield takeEvery(
    conferenceActions.startConferenceSessionAction().type,
    startConferenceSessionFlowSaga
  );
  yield takeEvery(
    conferenceActions.stopConferenceSessionAction().type,
    stopConferenceSessionFlowSaga
  );
  yield takeEvery(conferenceActions.blockUserAction().type, blockUserFlowSaga);
  yield takeEvery(conferenceActions.updateVodResourceAction().type, updateVodResourceFlowSaga);
  yield takeEvery(conferenceActions.askForHelpAction().type, askForHelpFlowSaga);
  yield call(initCacheSagaWatchers);
}
